import { env } from "./env";
import { t } from "@/lib/i18n";

/** Register this studio's worker copy with its own scope. Errors never block the app. */
export async function registerStudioWorker(slug: string): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator) || import.meta.env.DEV) return null;
  try {
    return await navigator.serviceWorker.register(`/s/${slug}/sw.js`, { scope: `/s/${slug}/` });
  } catch (e) {
    console.warn("Service worker registration failed", e);
    return null;
  }
}

export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
}

export type PushAvailability =
  | { state: "unsupported" }
  | { state: "needs-install" }
  | { state: "not-configured" }
  | { state: "denied" }
  | { state: "available" }
  | { state: "subscribed"; endpoint: string };

/** Why push is not offered, in the current language (resolved at render, not cached). */
export function pushReason(state: PushAvailability["state"]): string | null {
  const m = t().pwa;
  switch (state) {
    case "not-configured":
      return m.notConfigured;
    case "needs-install":
      return m.iosHome;
    case "unsupported":
      return m.unsupported;
    case "denied":
      return m.denied;
    default:
      return null;
  }
}

/** What push can honestly do on this device right now. */
export async function pushAvailability(slug: string): Promise<PushAvailability> {
  if (!env.VITE_VAPID_PUBLIC_KEY) return { state: "not-configured" };
  if (isIos() && !isStandalone()) return { state: "needs-install" };
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return { state: "unsupported" };
  if (Notification.permission === "denied") return { state: "denied" };
  const reg = await navigator.serviceWorker.getRegistration(`/s/${slug}/`);
  const sub = await reg?.pushManager.getSubscription();
  return sub ? { state: "subscribed", endpoint: sub.endpoint } : { state: "available" };
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Ask permission and create a subscription bound to this studio's worker. */
export async function subscribePush(slug: string): Promise<PushSubscriptionJSON> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("permission_denied");
  const reg = (await navigator.serviceWorker.getRegistration(`/s/${slug}/`)) ?? (await registerStudioWorker(slug));
  if (!reg) throw new Error("no_service_worker");
  await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(env.VITE_VAPID_PUBLIC_KEY!),
  });
  return sub.toJSON();
}

export async function unsubscribePush(slug: string): Promise<string | null> {
  const reg = await navigator.serviceWorker.getRegistration(`/s/${slug}/`);
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  return endpoint;
}
